import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Copy, Gift, Sparkles, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { devWarn } from "../lib/logger";

/**
 * Refer & Earn card — shows the signed-in user's referral link, count, and copy action.
 * Renders nothing (or a sign-in nudge) when the user is not logged in.
 */
export const ReferralCard = ({ compact = false }) => {
  const { user } = useAuth();
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        if (!cancelled) setRef(data);
      } catch (err) {
        devWarn("Referral fetch failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const displayUrl = ref
    ? `${window.location.origin}/?ref=${ref.code}`
    : "";

  const copy = useCallback(async () => {
    if (!displayUrl) return;
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy — long-press to copy");
    }
  }, [displayUrl]);

  const share = useCallback(async () => {
    if (!displayUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Numero by Auracean",
          text: "Get a free numerology reading — sign up with my link and we both earn 3 credits ✦",
          url: displayUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copy();
    }
  }, [displayUrl, copy]);

  if (!user || loading || !ref) return null;

  const bonus = ref.bonus_per_referral;

  return (
    <div
      data-testid="referral-card"
      className={`relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-[#161014] via-[#141419] to-[#0f0d17] ${
        compact ? "p-5" : "p-6 sm:p-8"
      }`}
    >
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="h-4 w-4 text-gold" />
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gold">
            Refer &amp; Earn
          </span>
        </div>
        <h3
          className={`font-serif tracking-tight mb-2 ${compact ? "text-2xl" : "text-3xl sm:text-4xl"}`}
        >
          Give {bonus} credits.<br />
          <span className="italic text-gold">Get {bonus} credits.</span>
        </h3>
        <p className={`text-neutral-400 mb-5 ${compact ? "text-xs" : "text-sm"}`}>
          Share your link. When a friend signs up and buys any pack, you both get{" "}
          <span className="text-white">{bonus} credits</span> added instantly.
        </p>

        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 sm:px-4 py-2 mb-4">
          <span
            data-testid="referral-link"
            className="flex-1 truncate text-xs sm:text-sm text-neutral-200 font-mono"
            title={displayUrl}
          >
            {displayUrl}
          </span>
          <Button
            type="button"
            size="sm"
            onClick={copy}
            data-testid="referral-copy"
            className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] h-8 px-3 shrink-0"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline ml-1.5 text-xs">
              {copied ? "Copied" : "Copy"}
            </span>
          </Button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-xs">
            <div data-testid="referral-earned" className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-gold" />
              <span className="text-white font-medium">{ref.credits_earned}</span>
              <span className="text-neutral-500">earned</span>
            </div>
            <div data-testid="referral-pending" className="text-neutral-500">
              {ref.pending_count} pending
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={share}
            data-testid="referral-share"
            className="rounded-full border-white/20 bg-transparent hover:bg-white/5 hover:text-white text-xs h-8"
          >
            <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
          </Button>
        </div>
      </div>
    </div>
  );
};
